"use client";

import { ChevronDown, Instagram, Youtube, Music2, Music } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const shopLinks = [{
  label: "Tree Removal",
  href: "/OurExpertisePage"
}, {
  label: "Precision Pruning",
  href: "/OurExpertisePage"
}, {
  label: "Stump Grinding",
  href: "/OurExpertisePage"
}, {
  label: "Emergency Care",
  href: "/OurExpertisePage"
}];
const supportLinks = [{
  label: "Service Areas",
  href: "#"
}, {
  label: "ISA Certification",
  href: "#"
}, {
  label: "Request Quote",
  href: "/GetQuotePage"
}, {
  label: "Contact Arborist",
  href: "#"
}];
const socialLinks = [{
  label: "Instagram",
  icon: Instagram,
  href: "#"
}, {
  label: "YouTube",
  icon: Youtube,
  href: "#"
}, {
  label: "TikTok",
  icon: Music2,
  href: "#"
}, {
  label: "Spotify",
  icon: Music,
  href: "#"
}];
const legalLinks = [{
  label: "Privacy Policy",
  href: "#"
}, {
  label: "Terms of Service",
  href: "#"
}, {
  label: "Insurance Info",
  href: "#"
}];
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[0, 1, 2, 3].map(i => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-muted animate-pulse" />
          </li>)}
      </ul>;
  }
  const items = menuItems.length > 0 ? menuItems : [{
    id: "1",
    label: "Our Story",
    href: "/OurStoryPage"
  }, {
    id: "2",
    label: "Tree Insights",
    href: "/TreeInsightsPage"
  }, {
    id: "3",
    label: "Careers",
    href: "#"
  }, {
    id: "4",
    label: "DFW Reputation",
    href: "#"
  }];
  return <ul className="space-y-3">
      {items.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="group inline-flex items-center gap-2 text-sm font-default text-foreground/80 hover:text-accent transition-colors">
            <span className="h-1 w-1 bg-accent opacity-0 group-hover:opacity-100 transition-opacity" />
            <span className="group-hover:translate-x-0.5 transition-transform">
              {item.label}
            </span>
          </Link>
        </li>)}
    </ul>;
}
export default function SiteFooter() {
  const {
    ref: wordmarkRef,
    isVisible: wordmarkVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2787" id="site-footer" className="bg-background text-foreground pt-[120px] pb-[60px] px-6 md:px-10 lg:px-16 border-t border-primary/10 relative overflow-hidden">
      {}
      <div ref={wordmarkRef} className={`group relative mb-12 transition-all duration-1000 ${wordmarkVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="font-default font-extrabold leading-[0.85] text-foreground select-none relative" style={{
        fontSize: "clamp(6rem, 15vw, 14rem)",
        letterSpacing: "-0.05em"
      }}>
          <span className="relative inline-block">
            ARBOR PRO
            <span className="text-accent group-hover:animate-pulse inline-block">
              .
            </span>
            {}
            <span aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 h-px w-0 group-hover:w-full bg-accent/40 transition-[width] duration-1000 ease-linear" />
          </span>
        </h2>
      </div>

      <div className="text-center mb-20">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/60">
          © 2025 ARBOR PRO TEXAS. PROFESSIONAL ARBORIST CARE.
        </p>
      </div>

      {}
      <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 transition-all duration-1000 delay-150 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-secondary mb-6 font-semibold">
            01 // Services
          </h3>
          <ul className="space-y-3">
            {shopLinks.map((link, i) => <li data-index={i} key={link.label}>
                <Link to={link.href} className="group inline-flex items-center gap-2 text-sm font-default text-foreground/80 hover:text-accent transition-colors">
                  <span className="h-1 w-1 bg-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-0.5 transition-transform">
                    {link.label}
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>

        {}
        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-secondary mb-6 font-semibold">
            02 // Support
          </h3>
          <ul className="space-y-3">
            {supportLinks.map((link, i) => <li data-index={i} key={link.label}>
                <Link to={link.href} className="group inline-flex items-center gap-2 text-sm font-default text-foreground/80 hover:text-accent transition-colors">
                  <span className="h-1 w-1 bg-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:translate-x-0.5 transition-transform">
                    {link.label}
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>

        {}
        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-secondary mb-6 font-semibold">
            03 // Company
          </h3>
          <MenuProvider menu_id="46">
            <FooterMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-secondary mb-6 font-semibold">
            04 // Connect
          </h3>
          <div className="flex items-center gap-3 mb-6">
            {socialLinks.map((social, i) => {
            const Icon = social.icon;
            return <Link data-index={i} key={social.label} to={social.href} aria-label={social.label} className="inline-flex items-center justify-center h-9 w-9 border border-border hover:border-accent hover:text-accent text-foreground/80 transition-colors rounded-md">
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                </Link>;
          })}
          </div>
          <address className="not-italic font-serif italic text-sm text-foreground/70 leading-relaxed">
            <div>Fort Worth, Texas</div>
            <div>Serving the DFW Metroplex</div>
            <div className="mt-2 font-mono not-italic text-foreground/80 flex flex-col gap-1">
              <Link to="tel:+18175033131" className="hover:text-accent transition-colors">
                (817) 503-3131
              </Link>
              <Link to="mailto:arborprotexas@gmail.com" className="hover:text-accent transition-colors">
                arborprotexas@gmail.com
              </Link>
            </div>
          </address>
        </div>
      </div>

      {}
      <div className="mt-16 mb-8 h-px w-full bg-border" />

      {}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        {}

        {}
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] font-mono">
          {legalLinks.map((link, i) => <div data-index={i} key={link.label} className="flex items-center gap-3">
              <Link to={link.href} className="text-foreground/60 hover:text-accent transition-colors">
                {link.label}
              </Link>
              {i < legalLinks.length - 1 && <span className="h-1 w-1 bg-accent" aria-hidden="true" />}
            </div>)}
        </div>

        {}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="border-border bg-transparent text-foreground hover:bg-muted hover:text-foreground text-[11px] uppercase tracking-[0.2em] font-mono h-9 px-3 rounded-md">
              TX / DFW
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover border-border text-popover-foreground rounded-md">
            <DropdownMenuItem className="text-[11px] uppercase tracking-[0.2em] font-mono">
              TX / DFW
            </DropdownMenuItem>
            <DropdownMenuItem className="text-[11px] uppercase tracking-[0.2em] font-mono">
              TX / AUSTIN
            </DropdownMenuItem>
            <DropdownMenuItem className="text-[11px] uppercase tracking-[0.2em] font-mono">
              TX / HOUSTON
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </section>;
}